import { mixpanelReset, mxp } from '@analytics/src/mixpanel';
import { useSession, useUser } from '@clerk/react';
import { EAccountStageLabel, User } from '@models/outseta/outseta';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from 'src/contexts/auth-context';
import { ClerkMeData, useClerkMe } from 'src/contexts/ClerkMeContext';
import { UserCountry } from 'src/models/abTest/experiment';
import { signOutClerk } from 'src/utils/clerkAuth';

interface ClerkIdentityUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  imageUrl: string;
  primaryEmailAddress?: { emailAddress: string } | null;
}

function marketToCountry(market?: string | null): UserCountry | null {
  switch (market?.toLowerCase()) {
    case 'us':
      return UserCountry.USA;
    case 'it':
      return UserCountry.ITA;
    case 'mea':
      return UserCountry.MEA;
    default:
      return null;
  }
}

function mapClerkUser(clerkUser: ClerkIdentityUser, me: ClerkMeData | undefined): User {
  const subscription = me?.subscription;
  const activeAccount = me?.activeAccount;
  const periodEnd = subscription?.currentPeriodEnd ?? '';
  return {
    Email: clerkUser.primaryEmailAddress?.emailAddress ?? '',
    FirstName: clerkUser.firstName ?? '',
    LastName: clerkUser.lastName ?? '',
    FullName: [clerkUser.firstName, clerkUser.lastName].filter(Boolean).join(' '),
    ProfileImageS3Url: clerkUser.imageUrl,
    Uid: clerkUser.id,
    HasUnsubscribed: subscription?.status === 'CANCELING' || subscription?.status === 'EXPIRED',
    Account: {
      Name: activeAccount?.name ?? '',
      Uid: activeAccount?.id ?? '',
      AccountStageLabel:
        subscription?.status === 'TRIALING'
          ? EAccountStageLabel.TRIAL
          : subscription?.status === 'CANCELING'
            ? EAccountStageLabel.UNSUBSCRIBED
            : '',
      CurrentSubscription: {
        Plan: { Uid: subscription?.planKey ?? '', Name: '' },
        RenewalDate: periodEnd,
        StartDate: subscription?.currentPeriodStart ?? '',
        EndDate: subscription?.cancelAt ?? null,
        BillingRenewalTerm: subscription?.interval === 'year' ? 12 : subscription?.interval === 'month' ? 1 : 0,
      },
    },
  } as User;
}

export function ClerkAuthStateProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, user: clerkUser } = useUser();
  const me = useClerkMe();
  const queryClient = useQueryClient();
  const { session } = useSession();
  useEffect(() => {
    queryClient.removeQueries({
      predicate: (query) =>
        query.queryKey[0] === 'pendingPurchase' &&
        (query.queryKey[1] !== clerkUser?.id || query.queryKey[2] !== session?.id),
    });
  }, [clerkUser?.id, queryClient, session?.id]);
  const navigate = useNavigate();
  const value = useMemo(() => {
    const accountData = me.data?.user.id === clerkUser?.id ? me.data : undefined;
    const status = accountData?.subscription?.status;
    return {
      user: clerkUser ? mapClerkUser(clerkUser, accountData) : null,
      isLoading: !isLoaded || (isSignedIn === true && me.isPending),
      hasUnsubscribed: status === 'CANCELING' || status === 'EXPIRED',
      isTrial: status === 'TRIALING',
      countryCode: marketToCountry(accountData?.activeAccount?.market),
      logout: async (reason?: 'token_expired' | 'manual') => {
        if ((reason ?? 'manual') !== 'manual') return;
        mixpanelReset();
        queryClient.removeQueries({ queryKey: ['pendingPurchase'], exact: false });
        await signOutClerk();
        queryClient.removeQueries({ queryKey: ['clerkMe'], exact: false });
        navigate('/auth', { replace: true });
      },
      jooiceFlow: null,
      setJooiceFlow: () => undefined,
    };
  }, [clerkUser, isLoaded, isSignedIn, me.data, me.isPending, navigate, queryClient]);

  useEffect(() => {
    const email = clerkUser?.primaryEmailAddress?.emailAddress;
    if (!email) return;
    const accountData = me.data?.user.id === clerkUser.id ? me.data : undefined;
    const subscription = accountData?.subscription;
    mxp.identify(email, {
      billing_stage: subscription?.status ?? 'unknown',
      plan_id: subscription?.planKey ?? '',
      outseta_country: marketToCountry(accountData?.activeAccount?.market) ?? undefined,
      trial_status: subscription?.status === 'TRIALING' ? 'active' : 'inactive',
    });
  }, [clerkUser, me.data]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
